// En-têtes de tableau collants — règle GÉNÉRALE de l'app (Ben 22/10/2026 :
// « les en-têtes de colonnes des tableaux longs restent visibles au
// défilement », tous tableaux, tous modules). Posée UNE fois par la coquille
// (`useEntetesCollants`, monté dans `App.tsx`), jamais module par module.
//
// Le CSS (`styles/globals.css`, sélecteurs de spécificité NULLE : un module
// qui stylise ses `th` garde la main) colle les `th` du `thead` selon le
// marquage posé ici sur chaque `<table>` de `<main>` :
//   - `page`      : le tableau défile avec la page → `top` = hauteur RÉELLE du
//                   chrome de l'app (`--app-entete-h`, mesurée sur la TopBar) ;
//   - `conteneur` : le tableau vit dans un conteneur à défilement vertical
//                   interne (TableContainer max-h, tiroir, dialogue) → `top: 0`
//                   relatif à ce conteneur ;
//   - `aucun`     : un enrouleur à défilement HORIZONTAL déborde réellement
//                   (tableau plus large que l'écran) : `position: sticky` y est
//                   impossible par rapport à la page, on ne colle pas plutôt
//                   que de décaler l'en-tête sur les premières lignes.
//
// Un enrouleur `overflow-x: auto` / `overflow: hidden` qui NE déborde PAS
// (cas courant à 1280 : `.tscroll`, cartes arrondies) est un « conteneur de
// défilement » au sens CSS : il capturerait le sticky. On le marque
// `data-defilement-libere` → `overflow: clip` (même rognage, mais plus de
// conteneur de défilement), `display: flow-root` s'il était `block` (garde le
// contexte de formatage que créait `overflow`). Réévalué à chaque changement :
// s'il se met à déborder, il retrouve son `overflow` d'origine.
//
// Fiches (< 640 px) : rien de collant, aucun enrouleur touché.

export type ModeEntete = 'page' | 'conteneur' | 'aucun';

export const ATTR_MODE = 'data-entete-collant';
export const ATTR_LIBERE = 'data-defilement-libere';
export const VAR_HAUTEUR = '--app-entete-h';
export const VAR_FOND = '--entete-collant-fond';
export const VAR_DECALAGE = '--entete-decalage';
/** Sous cette largeur, les tableaux passent en fiches : pas d'en-tête collant. */
export const LARGEUR_MIN = 640;

const DEFILE = new Set(['auto', 'scroll', 'hidden', 'overlay']);
const TRANSPARENT = new Set(['transparent', 'rgba(0, 0, 0, 0)', '']);

/** Fond opaque effectif de l'en-tête : celui du `th`, sinon du premier parent (tr, thead, table…) qui en a un. */
export function fondEffectif(th: Element, style: (e: Element) => CSSStyleDeclaration): string {
  for (let e: Element | null = th; e; e = e.parentElement) {
    const f = style(e).backgroundColor;
    if (!TRANSPARENT.has(f)) return f;
  }
  return '#ffffff';
}

function dansSuperposition(e: Element, style: (e: Element) => CSSStyleDeclaration): boolean {
  for (let x: Element | null = e; x; x = x.parentElement) if (style(x).position === 'fixed') return true;
  return false;
}

/**
 * Mode d'un tableau et enrouleurs à libérer, en remontant de la table jusqu'à
 * `racine` (exclue). Lit le DOM, n'écrit rien.
 */
export function classerTable(
  table: HTMLTableElement,
  racine: Element,
  style: (e: Element) => CSSStyleDeclaration = (e) => getComputedStyle(e),
): { mode: ModeEntete; liberer: HTMLElement[] } {
  const liberer: HTMLElement[] = [];
  for (let e = table.parentElement; e && e !== racine; e = e.parentElement) {
    const s = style(e);
    if (!DEFILE.has(s.overflowX) && !DEFILE.has(s.overflowY)) continue;
    const deborde = e.scrollHeight > e.clientHeight + 1;
    // Défilement vertical voulu (hauteur bornée, contenu qui dépasse, ou superposition) : collé dans le conteneur.
    if (deborde || s.maxHeight !== 'none' || dansSuperposition(e, style)) return { mode: 'conteneur', liberer: [] };
    // Enrouleur horizontal qui déborde vraiment : impossible de coller à la page.
    if (e.scrollWidth > e.clientWidth + 1) return { mode: 'aucun', liberer: [] };
    liberer.push(e);
  }
  return { mode: 'page', liberer };
}

/** Lignes du `thead` direct du tableau (pas celles d'un tableau imbriqué). */
function lignesEntete(t: HTMLTableElement): HTMLTableRowElement[] {
  const thead = [...t.children].find((c) => c.tagName === 'THEAD');
  return thead ? ([...thead.children].filter((c) => c.tagName === 'TR') as HTMLTableRowElement[]) : [];
}

/** Applique le marquage à tous les tableaux à en-tête de `racine`. */
export function marquerTableaux(racine: Element, largeur: number, style?: (e: Element) => CSSStyleDeclaration): void {
  const lire = style ?? ((e: Element) => getComputedStyle(e));
  // On repart d'un état neutre pour mesurer les débordements avec l'overflow d'origine.
  for (const e of racine.querySelectorAll(`[${ATTR_LIBERE}]`)) e.removeAttribute(ATTR_LIBERE);
  const tables = [...racine.querySelectorAll<HTMLTableElement>('table')].filter((t) => lignesEntete(t).length > 0);
  if (largeur < LARGEUR_MIN) {
    for (const t of tables) t.removeAttribute(ATTR_MODE);
    return;
  }
  for (const t of tables) {
    const { mode, liberer } = classerTable(t, racine, lire);
    if (t.getAttribute(ATTR_MODE) !== mode) t.setAttribute(ATTR_MODE, mode);
    for (const e of liberer) e.setAttribute(ATTR_LIBERE, lire(e).display === 'block' ? 'bloc' : '');
    const lignes = lignesEntete(t);
    const th = lignes[0]!.querySelector('th');
    if (th && mode !== 'aucun') {
      const fond = fondEffectif(th, lire);
      if (t.style.getPropertyValue(VAR_FOND) !== fond) t.style.setProperty(VAR_FOND, fond);
    }
    // En-tête à plusieurs lignes : chaque ligne colle sous les précédentes.
    let decalage = 0;
    for (const [i, tr] of lignes.entries()) {
      const v = `${decalage}px`;
      if (i > 0 && tr.style.getPropertyValue(VAR_DECALAGE) !== v) tr.style.setProperty(VAR_DECALAGE, v);
      decalage += tr.getBoundingClientRect().height;
    }
  }
}
